/**
 * دوستان — قرینه‌ی apps/mobile/app/(tabs)/friends.tsx + friend/add.tsx (بخشِ
 * نامِ‌کاربری‌اش).
 *
 * تفاوتِ عمدی با موبایل: **دفترچه‌ی گوشی اینجا نیست**. مرورگر به مخاطبین
 * دسترسی ندارد، پس هیچ ردیفی از راهِ ایمپورتِ دفترچه به این صفحه نمی‌رسد.
 *
 * ولی از مایگریشن 0075 «آدمی که عضو نیست» روی وب هم وجود دارد: دعوت کردنِ
 * کسی با ایمیل، خودکار او را به فهرستِ دوستان اضافه می‌کند. آن ردیف‌ها از
 * `contactFriendRepo` می‌آیند و کنارِ دوستانِ اپی می‌نشینند، با نشانِ «هنوز
 * عضو نیست» — چون تا وقتی حساب ندارند کسی آن‌طرف نیست که درخواستی را قبول
 * کند، و «منتظرِ تایید» نشان دادنشان دروغ است. لحظه‌ای که عضو شوند، از این
 * فهرست می‌روند به «درخواست فرستاده شد» و باید جواب بدهند.
 *
 * `groupMemberCount`/`membersFirst`/`pickCandidates` از core می‌آیند — همان
 * منطقی که موبایل استفاده می‌کند، همان دو باگی که یک‌بار رفع شدند.
 *
 * ═══ چیدمان: دوباره دو ستون، این بار درست (WHOSUP-157) ═══
 *
 * در تسک T07 این صفحه تک‌ستونه شد، چون آن‌موقع داخلِ پنلِ ۳۴۰ پیکسلیِ نوارِ
 * کناری رندر می‌شد و گریدِ `.two-col` با `@media` عرضِ *پنجره* را می‌دید نه
 * عرضِ *ظرف* — دو ستون در ~۳۰۰ پیکسل له می‌شدند. WHOSUP-157 آن نوار را حذف
 * کرد و این صفحه حالا همیشه تمام‌عرض است، پس همان مشکل دیگر وجود ندارد:
 * گروه‌ها و آدم‌ها دو ستونِ کنارِ هم‌اند (`.friends-cols`، مثل اپ) و روی
 * صفحه‌ی باریک با media query — که حالا واقعاً عرضِ ظرف است — زیرِ هم می‌روند.
 *
 * **گروه همچنان سربرگِ آدم‌هاست، نه یک فهرستِ موازی**: هر گروه باز می‌شود و
 * اعضایش زیرش می‌آیند؛ چیدنِ اعضا همان‌جا داخلِ گروه انجام می‌شود، بی‌مودال.
 *
 * توجه: فهرستِ اعضای هر گروه فقط دوستانِ اپ را نشان می‌دهد، ولی عددِ کنارِ
 * نام `groupMemberCount` است که مخاطبینِ گوشی را هم می‌شمارد (گروهی که روی
 * موبایل ساخته شده می‌تواند عضوِ غیرِاپی داشته باشد). پس عدد می‌تواند از
 * تعدادِ ردیف‌ها بیشتر باشد — همان تفاوتِ عمدیِ بالای فایل، نه یک باگ.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { friendRepo, groupRepo } from '@/core/data/socialRepo';
import {
  contactFriendRepo, type ContactFriend, type ContactFriendEdit,
} from '@/core/data/contactFriendRepo';
import { isDuplicatePhoneError } from '@/core/lib/contacts';
import { notificationRepo, tagRepo } from '@/core/data/miscRepo';
import { useSession } from '@/core/session';
import { t, useLocale } from '@/core/lib/i18n';
import { notifTitle } from '@/core/lib/notifText';
import { tagLabel } from '@/core/lib/tagLabel';
import { suggestTags, hasTagSuggestQuery } from '@/core/lib/tagFilter';
import { groupMemberCount, membersFirst, pickCandidates } from '@/core/lib/roster';
import { contactFriendsToShow } from '@/core/lib/friendList';
import { reportError } from '@/core/lib/report';
import { PushOfferCard } from '@/features/notifications/PushOfferCard';
import { ReportButton } from '@/features/report/ReportMenu';
import { BlockButton } from '@/features/blocks/BlockDialog';
import { Avatar, Btn, Card, Checkbox, Field, Note } from '@/ui/kit';
import { PageBanner } from '@/ui/PageBanner';
import { ChevronIcon, PencilIcon, TrashIcon } from '@/ui/icons';
import type { Friend, Group } from '@/core/domain/types';

const EMOJIS = ['🏷', '🥾', '🎬', '🍕', '☕️', '🎲', '⚽️', '🎸', '👨‍👩‍👧', '💼'];

type GroupForm = { id?: number; name: string; emoji: string; categoryId?: number | null };

/**
 * فرمِ «افزودن دوست» (WHOSUP-148): یک فرم، همه‌ی راه‌ها با هم.
 *
 * کاربر هر چه از طرف دارد — اسم، شماره، ایمیل، نامِ کاربری — یک‌جا می‌دهد
 * و خودِ اپ تصمیم می‌گیرد: عضو باشد درخواستِ دوستی می‌رود (با اسمی که خودِ
 * او انتخاب کرده)، نباشد دوستِ غیرِاپی ساخته می‌شود. حالت‌های جدا و لینکِ
 * «وارد کردنِ دستی» به خواستِ صریحِ ریویو حذف شدند. گروه‌های تیک‌خورده هم
 * همان‌جا عضو می‌شوند.
 */
type AddForm = { name: string; phone: string; email: string; username: string; groupIds: number[] };
const EMPTY_ADD: AddForm = { name: '', phone: '', email: '', username: '', groupIds: [] };

/**
 * کدام گروه‌ها بازند — عمداً بیرونِ کامپوننت.
 *
 * پنلِ کناری با هر بار رفتن به صفحه‌ای دیگر unmount می‌شود، و اگر این توی
 * `useState` بود هر بار همه‌ی گروه‌ها بسته برمی‌گشتند. همان الگوی
 * `features/signals/createPrefill.ts`: یک متغیرِ ماژول، که تا وقتی تبِ مرورگر
 * باز است می‌ماند و با رفرش پاک می‌شود.
 */
const openGroupIds = new Set<number>();

/**
 * فرمِ دوستِ غیرِاپی: همون `ContactFriendEdit` به‌علاوه‌ی شناسه — که وقتی
 * نباشد یعنی «افزودن» (WHOSUP-148)، نه ویرایش. یک فرم برای هر دو، چون
 * فیلدها همان سه‌تاست.
 */
type ContactEditForm = ContactFriendEdit & { id?: number };

/**
 * ردیف‌های تکرارشونده‌ی یک فیلدِ چندمقداری (شماره‌ها، ایمیل‌ها) — قرینه‌ی
 * `MultiField` موبایل (friend/add.tsx). همون رفتار: تک‌ردیفِ خالی می‌مونه،
 * ✕ فقط وقتی هست که چیزی برای پاک کردن باشه.
 */
function MultiField({ label, values, placeholder, type = 'text', onChange }: {
  label: string;
  values: string[];
  placeholder: string;
  type?: 'text' | 'email';
  onChange: (next: string[]) => void;
}) {
  const setAt = (i: number, v: string) => onChange(values.map((x, j) => (j === i ? v : x)));
  const removeAt = (i: number) => onChange(values.length === 1 ? [''] : values.filter((_, j) => j !== i));

  return (
    <>
      <span className="field-label">{label}</span>
      {values.map((v, i) => (
        <div key={i} className="multirow">
          <input className="input" type={type} placeholder={placeholder} value={v}
            onChange={(e) => setAt(i, e.target.value)} />
          {values.length > 1 || v ? (
            <button type="button" className="chip" aria-label={t('remove')}
              onClick={() => removeAt(i)}>✕</button>
          ) : null}
        </div>
      ))}
      <button type="button" className="linklike" onClick={() => onChange([...values, ''])}>
        {t('addAnother')}
      </button>
    </>
  );
}

export function FriendsPage() {
  useLocale();
  const { profile } = useSession();
  const qc = useQueryClient();

  /** فرمِ افزودن دوست؛ null یعنی مودال بسته است. */
  const [addForm, setAddForm] = useState<AddForm | null>(null);
  // پیامِ نتیجه‌ی افزودن دوست، با این‌که موفق بود یا نه — چون شکلِ نمایشش
  // فرق می‌کند: موفقیت بنرِ سبزِ برجسته است، بقیه هشدار (WHOSUP-149).
  const [requestMsg, setRequestMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [groupForm, setGroupForm] = useState<GroupForm | null>(null);
  /** دوستِ غیرِاپی‌ای که همین حالا ویرایش (WHOSUP-144) یا ساخته (WHOSUP-148) می‌شود. */
  const [contactEdit, setContactEdit] = useState<ContactEditForm | null>(null);
  const [contactEditMsg, setContactEditMsg] = useState<string | null>(null);
  /** گروهی که همین حالا دارد عضو اضافه می‌کند؛ هر بار فقط یکی. */
  const [addingFor, setAddingFor] = useState<number | null>(null);
  const [memberQ, setMemberQ] = useState('');
  // فقط برای اینکه باز/بسته شدنِ گروه رندرِ تازه بگیرد؛ خودِ حالت در
  // `openGroupIds` می‌نشیند که از عمرِ کامپوننت بلندتر است.
  const [, bump] = useState(0);

  const toggleGroup = (id: number) => {
    if (openGroupIds.has(id)) openGroupIds.delete(id);
    else openGroupIds.add(id);
    if (addingFor !== id) { setAddingFor(null); setMemberQ(''); }
    bump((n) => n + 1);
  };

  // «فلانی هم اومد» و «دوستیت قبول شد» با دیدنِ همین صفحه خونده می‌شن.
  // درخواستِ دوستی نه — بالای همین صفحه منتظرِ جوابه و تا جواب ندی نقطه‌ش می‌مونه.
  useEffect(() => {
    if (!profile) return;
    notificationRepo.markSeen(profile.id, 'friend')
      .then(() => qc.invalidateQueries({ queryKey: ['unreadByCategory'] }))
      .catch(() => undefined);
  }, [profile?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const friends = useQuery({
    queryKey: ['friends'],
    queryFn: () => friendRepo.list(profile!.id),
    enabled: !!profile,
  });
  const groups = useQuery({
    queryKey: ['groups'],
    queryFn: () => groupRepo.list(profile!.id),
    enabled: !!profile,
  });
  const tags = useQuery({ queryKey: ['tags'], queryFn: tagRepo.list, enabled: !!profile });

  /** آدم‌هایی که دعوتشان کرده‌ام ولی هنوز حساب ندارند (مایگریشن 0075). */
  const contactFriends = useQuery({
    queryKey: ['contactFriends'],
    queryFn: contactFriendRepo.list,
    enabled: !!profile,
  });

  /**
   * خبرِ «قبول نکرد».
   *
   * تنها جاییست که این اتفاق نوشته می‌شود: ردیفِ دوستیِ ردشده از
   * `friendRepo.list` بیرون است، پس بدونِ این کارت آن اسم بی‌هیچ توضیحی از
   * فهرست غیب می‌شد. عمداً با نگاه کردن پاک نمی‌شود (اقدام‌خواه است در
   * `ACTIONABLE_KINDS`)؛ با زدنِ «باشه» خوانده می‌شود.
   */
  const notifs = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationRepo.list(profile!.id),
    enabled: !!profile,
  });
  const declined = useMemo(
    () => (notifs.data ?? []).filter((n) => n.kind === 'friend_declined' && !n.readAt),
    [notifs.data],
  );

  const accepted = useMemo(
    () => (friends.data ?? []).filter((f) => f.status === 'accepted')
      .sort((a, b) => a.profile.displayName.localeCompare(b.profile.displayName)),
    [friends.data],
  );
  const pendingIn = useMemo(
    () => (friends.data ?? []).filter((f) => f.status === 'pending_in'), [friends.data],
  );
  const pendingOut = useMemo(
    () => (friends.data ?? []).filter((f) => f.status === 'pending_out'), [friends.data],
  );

  /**
   * فهرستِ آدم‌ها: دوستانِ اپی + کسانی که دعوت شده‌اند و هنوز عضو نیستند.
   *
   * `contactFriendsToShow` (در core) هر کسی را که حساب دارد از این فهرست
   * بیرون می‌گذارد، چون ردیفِ دوستی نماینده‌اش است — یا بالای صفحه منتظرِ
   * جواب است یا پایین توی «درخواست فرستاده شد». ردیفِ دوستی ندارد؟ پس دوست
   * نیست — نه «دوستِ بی‌تایید» (WHOSUP-261). همان قاعده که موبایل هم اجرا
   * می‌کند، از همان یک فایل.
   */
  const people = useMemo(() => {
    const rows: ({ key: string; name: string } & (
      | { kind: 'app'; friend: Friend }
      | { kind: 'contact'; contact: ContactFriend }))[] = [
      ...accepted.map((f) => ({
        key: `app-${f.friendshipId}`, name: f.profile.displayName, kind: 'app' as const, friend: f,
      })),
      ...contactFriendsToShow(contactFriends.data ?? []).map((c) => ({
        key: `contact-${c.id}`, name: c.name, kind: 'contact' as const, contact: c,
      })),
    ];
    return rows.sort((a, b) => a.name.localeCompare(b.name));
  }, [accepted, contactFriends.data]);

  const groupList = groups.data ?? [];

  /**
   * برای هر دوست، نامِ گروه‌هایی که عضوشان است.
   *
   * زیرِ نامِ آدم می‌نشیند تا فهرستِ پایینِ صفحه هم بگوید کی کجاست — بدون
   * اینکه لازم باشد همه‌ی گروه‌ها را باز کنی. یک بار ساخته می‌شود، نه به‌ازای
   * هر ردیف.
   */
  const groupsOfUser = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const g of groupList) {
      for (const uid of g.memberUserIds) {
        const arr = m.get(uid);
        if (arr) arr.push(g.name);
        else m.set(uid, [g.name]);
      }
    }
    return m;
  }, [groupList]);

  const invalidate = (key: string) => qc.invalidateQueries({ queryKey: [key] });

  const act = useMutation({
    mutationFn: (args: { id: number; accept: boolean }) => (args.accept
      ? friendRepo.accept(args.id) : friendRepo.decline(args.id)),
    onSuccess: (_r, args) => {
      invalidate('friends');
      // قبولِ اولین دوستی = لحظه‌ی قرارِ WHOSUP-71: کارتِ پیشنهادِ اعلان
      // همین‌جا و همین حالا باید بتواند دربیاید.
      if (args.accept) invalidate('hasFirstActivity');
    },
    onError: (e) => reportError('web.friends.act', e),
  });

  const dropFriend = useMutation({
    mutationFn: (friendshipId: number) => friendRepo.remove(friendshipId),
    // گروه‌ها هم: حذفِ دوست او را از گروه‌های هر دو طرف بیرون می‌برد (0153).
    onSuccess: () => { invalidate('friends'); invalidate('groups'); },
    onError: (e) => reportError('web.friends.remove', e),
  });

  /** برداشتنِ کسی که هنوز عضو نیست — ردیفش می‌ماند، فقط دیگر دوست نیست. */
  const dropContactFriend = useMutation({
    mutationFn: (id: number) => contactFriendRepo.remove(id),
    onSuccess: () => invalidate('contactFriends'),
    onError: (e) => reportError('web.friends.removeContact', e),
  });

  /**
   * ذخیره‌ی دوستِ غیرِاپی — ویرایش وقتی شناسه دارد، افزودن وقتی ندارد
   * (WHOSUP-148). `friends` هم تازه می‌شود چون بعدِ ذخیره
   * `match_my_contacts` اجرا شده و ایمیل/شماره‌ی تازه ممکن است به یک عضو
   * بخورد — که آن‌وقت ردیفِ این آدم جای دیگری از صفحه می‌نشیند.
   */
  const saveContactEdit = useMutation({
    mutationFn: async (f: ContactEditForm): Promise<void> => {
      if (f.id != null) await contactFriendRepo.update(f.id, f);
      else await contactFriendRepo.create(f);
    },
    onSuccess: () => {
      setContactEdit(null);
      invalidate('contactFriends');
      invalidate('friends');
    },
    onError: (e) => {
      // شماره‌ی تکراری (مایگریشن 0048): «دوباره امتحان کن» دروغه — باید
      // گفت دقیقاً چی شد. بقیه‌ی خطاها همون پیامِ عمومی.
      if (isDuplicatePhoneError(e)) { setContactEditMsg(t('contactDuplicatePhone')); return; }
      reportError('web.friends.editContact', e);
      setContactEditMsg(t('errorRetry'));
    },
  });

  /** «باشه» روی خبرِ رد شدن: تنها کاری که ازش برمی‌آید، خواندنش است. */
  const dismissDeclined = useMutation({
    mutationFn: (id: number) => notificationRepo.markRead(id),
    onSuccess: () => { invalidate('notifications'); invalidate('unreadByCategory'); },
    onError: (e) => reportError('web.friends.dismissDeclined', e),
  });

  /**
   * افزودنِ یک آدم — هر چه ازش داری، یک‌جا (WHOSUP-148).
   *
   * تصمیمِ «درخواست یا مخاطبِ غیرِاپی» مالِ core است (friendRepo.addPerson)؛
   * این‌جا فقط پیامِ «چی شد» ساخته می‌شود — با اسمی که خودِ طرف انتخاب
   * کرده، نه اسمی که فرستنده تایپ کرده — و گروه‌های تیک‌خورده عضو می‌شوند.
   * پیامِ خوش‌بینانه برای کاری که نشده، بدترین حالت است؛ هر نتیجه پیامِ
   * خودش را دارد.
   */
  const addPerson = useMutation({
    mutationFn: async (f: AddForm) => {
      const outcome = await friendRepo.addPerson(profile!.id, f);
      if (outcome.kind === 'self') return outcome;
      // عضویتِ گروه‌های انتخاب‌شده: عضوِ اپی با userId (سیاستِ gm_ins مالکِ
      // گروه را قبول می‌کند)، غیرِاپی با contactId. شکستِ یک گروه بقیه و
      // خودِ افزودن را نمی‌شکند — گزارش می‌شود و بس.
      const ref = outcome.kind === 'created'
        ? { kind: 'contact' as const, contactId: outcome.contactId }
        : { kind: 'user' as const, userId: outcome.userId };
      for (const g of groupList) {
        if (!f.groupIds.includes(g.id)) continue;
        const isIn = ref.kind === 'contact'
          ? g.memberContactIds.includes(ref.contactId)
          : g.memberUserIds.includes(ref.userId);
        if (isIn) continue;
        await groupRepo.addMember(g.id, ref)
          .catch((e) => reportError('web.friends.addPersonGroup', e));
      }
      return outcome;
    },
    onSuccess: (outcome, f) => {
      if (outcome.kind === 'self') {
        setRequestMsg({ ok: false, text: t('cantAddYourself') });
        return;
      }
      invalidate('friends');
      invalidate('contactFriends');
      invalidate('groups');
      // اولین درخواستِ دوستی هم لحظه‌ی قرارِ WHOSUP-71 است — مثلِ act بالا.
      invalidate('hasFirstActivity');
      setAddForm(EMPTY_ADD);
      // «شد» بنرِ سبزِ برجسته است، «از قبل دوستید» هشدار (WHOSUP-149).
      setRequestMsg(outcome.kind === 'already'
        ? { ok: false, text: t('alreadyFriendsMsg') }
        : {
          ok: true,
          text: outcome.kind === 'created'
            ? t('friendAdded', { name: f.name.trim() })
            : t('addFoundOnApp', { name: outcome.displayName }),
        });
    },
    onError: (e) => {
      // شماره‌ی تکراری (مایگریشن 0048): «دوباره امتحان کن» دروغه.
      if (isDuplicatePhoneError(e)) {
        setRequestMsg({ ok: false, text: t('contactDuplicatePhone') });
        return;
      }
      reportError('web.friends.addPerson', e);
      setRequestMsg({ ok: false, text: t('requestError') });
    },
  });

  const toggleMember = useMutation({
    mutationFn: (args: { group: Group; friend: Friend }) => {
      const isIn = args.group.memberUserIds.includes(args.friend.profile.id);
      const ref = { kind: 'user' as const, userId: args.friend.profile.id };
      return isIn ? groupRepo.removeMember(args.group.id, ref) : groupRepo.addMember(args.group.id, ref);
    },
    onSuccess: () => invalidate('groups'),
    onError: (e) => reportError('web.friends.toggleMember', e),
  });

  const saveGroup = useMutation({
    mutationFn: (f: GroupForm) => (f.id
      ? groupRepo.rename(f.id, f.name.trim(), f.emoji, f.categoryId).then(() => f.id!)
      : groupRepo.create(profile!.id, f.name.trim(), f.emoji, f.categoryId)),
    onSuccess: (id) => {
      // گروهِ تازه باز باز می‌شود: بلافاصله بعدِ ساختن، کارِ بعدی ریختنِ آدم
      // توش است و بسته بودنش یعنی یک کلیکِ اضافه به هیچ.
      if (typeof id === 'number') openGroupIds.add(id);
      setGroupForm(null);
      invalidate('groups');
    },
    onError: (e) => reportError('web.friends.saveGroup', e),
  });

  const dropGroup = useMutation({
    mutationFn: (g: Group) => groupRepo.remove(g.id),
    onSuccess: () => invalidate('groups'),
    onError: (e) => reportError('web.friends.dropGroup', e),
  });

  /**
   * کاندیداهای افزودن به گروهِ بازِ فعلی.
   *
   * `pickCandidates` روی `{ name }` کار می‌کند (همون قراردادِ core)؛ اینجا
   * فقط یه دور نگاشت لازمه چون Friend اسمش را زیرِ profile.displayName دارد.
   */
  const addingGroup = addingFor == null
    ? null : groupList.find((g) => g.id === addingFor) ?? null;
  const pickerRows = useMemo(() => {
    if (!addingGroup) return [];
    const pool = accepted.map((f) => ({ name: f.profile.displayName, friend: f }));
    return pickCandidates({
      pool,
      friends: pool,
      isMember: (r) => addingGroup.memberUserIds.includes(r.friend.profile.id),
      query: memberQ,
    }).map((r) => r.friend);
  }, [addingGroup, accepted, memberQ]);

  /**
   * تگ‌های فرمِ گروه (WHOSUP-151): تا وقتی کلمه‌ای از اسمِ گروه به ۳ حرف
   * نرسیده هیچ تگی نشون داده نمی‌شه؛ بعدش فقط تگ‌هایی که یکی از کلمه‌های
   * تایپ‌شده رو دارن. تگِ انتخاب‌شده‌ی فعلی (موقعِ ویرایش) همیشه می‌مونه،
   * وگرنه راهی برای دیدن/برداشتنش نیست.
   */
  const groupTagChoices = useMemo(() => {
    if (!groupForm) return [];
    const all = tags.data ?? [];
    const shown = suggestTags(all, groupForm.name);
    const sel = groupForm.categoryId == null
      ? undefined : all.find((x) => x.id === groupForm.categoryId);
    return sel && !shown.some((x) => x.id === sel.id) ? [sel, ...shown] : shown;
  }, [groupForm, tags.data]);

  const removeFriend = (f: Friend) => {
    // eslint-disable-next-line no-alert
    if (window.confirm(t('removeFriendConfirm', { name: f.profile.displayName }))) {
      dropFriend.mutate(f.friendshipId);
    }
  };

  return (
    <div className="feed friends">
      {/* بنرِ معرفی (WHOSUP-157) — فقط روی صفحه‌ی پهن؛ خودش تصمیم می‌گیرد. */}
      <PageBanner kind="friends" />

      <div className="friends-head">
        <h1 className="feed-title">{t('friends')}</h1>
        <Btn kind="ghost" label={t('addFriend')} onClick={() => {
          setRequestMsg(null); setAddForm(EMPTY_ADD);
        }} />
      </div>

      {/* قرارِ WHOSUP-71: پیشنهادِ اعلان با اولین درخواست/قبولِ دوستی —
          یعنی همین صفحه، همان لحظه. خودش تصمیم می‌گیرد دیده شود یا نه. */}
      <PushOfferCard />

      {/* ---------- درخواست‌های رسیده ---------- */}
      {pendingIn.length > 0 ? (
        <>
          <h3>{t('incomingRequests')}</h3>
          {pendingIn.map((f) => (
            <Card key={f.friendshipId} className="request-card">
              <div className="signal-main">
                <span>{f.profile.displayName}</span>
                <span className="dim">@{f.profile.username}</span>
              </div>
              <div className="chiprow">
                <Btn label={t('accept')}
                  disabled={act.isPending}
                  onClick={() => act.mutate({ id: f.friendshipId, accept: true })} />
                <Btn kind="ghost" label={t('decline')}
                  disabled={act.isPending}
                  onClick={() => act.mutate({ id: f.friendshipId, accept: false })} />
                {/* مسدود کردن مالِ هر دو طرفِ یک درخواست است، نه فقط دوستِ
                    تاییدشده (WHOSUP-261). */}
                <BlockButton person={{ id: f.profile.id, name: f.profile.displayName }} />
              </div>
            </Card>
          ))}
        </>
      ) : null}

      {/* ---------- «قبول نکرد» ---------- */}
      {/* کنارِ درخواست‌های رسیده، چون هر دو درباره‌ی جوابِ یک درخواست‌اند —
          یکی جوابی که باید بدهی، یکی جوابی که گرفتی. */}
      {declined.map((n) => (
        <Card key={n.id} className="friend-row">
          <span className="person-main">
            {/* جمله را سرور ساخته، ولی از روی کلیدش به زبانِ امروزِ اپ
                دوباره ساخته می‌شود (مایگریشن ۰۱۱۲) — نگاه کن به یادداشتِ
                core/lib/notifText. چیزی که نباید برگردد:
                `t('notifFriendDeclined', { name: n.title })`، که حالا جمله
                را داخلِ جمله می‌پیچید. */}
            <span className="person-sub">{notifTitle(n)}</span>
          </span>
          <Btn kind="ghost" label={t('gotIt')}
            disabled={dismissDeclined.isPending}
            onClick={() => dismissDeclined.mutate(n.id)} />
        </Card>
      ))}

      {/* ---------- دو ستون: گروه‌ها | آدم‌ها (WHOSUP-157) ----------
          درخواست‌ها و خبرِ ردشدن بالای هر دو ستون ماندند: جواب‌خواه‌اند و
          نباید تهِ یک ستون گم شوند — روی صفحه‌ی باریک هم همان بالای صفحه‌اند،
          همان‌جایی که قبل از این چیدمان بودند. */}
      <div className="friends-cols">
      <section className="friends-col">
      <h3>{t('myGroups')}</h3>
      {groupList.length === 0 ? <Note>{t('noGroupsYet')}</Note> : null}

      {groupList.map((g) => {
        const open = openGroupIds.has(g.id);
        const members = accepted.filter((f) => g.memberUserIds.includes(f.profile.id));
        const adding = addingFor === g.id;
        return (
          <div key={g.id} className={`grp${open ? ' open' : ''}`}>
            <button type="button" className="grp-head" aria-expanded={open}
              onClick={() => toggleGroup(g.id)}>
              <span className="group-mark">{g.emoji || g.name.trim()[0]?.toUpperCase()}</span>
              <span className="grp-name">
                {g.name} <span className="dim">({groupMemberCount(g)})</span>
              </span>
              <ChevronIcon className="grp-caret" />
            </button>

            {open ? (
              <div className="grp-body">
                {members.length === 0 && !adding
                  ? <p className="dim">{t('noFriendsForGroup')}</p> : null}

                {/* اعضای فعلی — چیپ برمی‌داردشان، دقیقاً مثلِ مودالِ قبلی */}
                {!adding ? members.map((f) => (
                  <div key={f.friendshipId} className="person">
                    <Avatar url={f.profile.avatarUrl} name={f.profile.displayName} size={26} />
                    <span className="person-main">
                      <span className="person-name">{f.profile.displayName}</span>
                    </span>
                    <button type="button" className="chip on"
                      onClick={() => toggleMember.mutate({ group: g, friend: f })}>
                      {t('inGroup')}
                    </button>
                  </div>
                )) : null}

                {/* حالتِ افزودن: جستجو + همه‌ی دوستان، اعضا اول */}
                {adding ? (
                  <>
                    <input className="input" placeholder={t('contactsSearch')} value={memberQ}
                      onChange={(e) => setMemberQ(e.target.value)} />
                    {accepted.length === 0 ? <Note>{t('noFriendsForGroup')}</Note> : null}
                    {membersFirst(pickerRows, (f) => g.memberUserIds.includes(f.profile.id))
                      .map((f) => {
                        const inIt = g.memberUserIds.includes(f.profile.id);
                        return (
                          <button key={f.friendshipId} type="button" className="member-row"
                            onClick={() => toggleMember.mutate({ group: g, friend: f })}>
                            <span>{f.profile.displayName}</span>
                            <span className={`chip${inIt ? ' on' : ''}`}>
                              {inIt ? t('inGroup') : t('notInGroup')}
                            </span>
                          </button>
                        );
                      })}
                  </>
                ) : null}

                <div className="grp-actions">
                  {adding ? (
                    <Btn kind="ghost" label={t('doneCheck')}
                      onClick={() => { setAddingFor(null); setMemberQ(''); }} />
                  ) : (
                    <Btn kind="ghost" label={t('addSomeone')}
                      onClick={() => { setAddingFor(g.id); setMemberQ(''); }} />
                  )}
                  <span className="chiprow">
                    <button type="button" className="linklike"
                      onClick={() => setGroupForm({
                        id: g.id, name: g.name, emoji: g.emoji, categoryId: g.categoryId ?? null,
                      })}>
                      {t('groupSettings')}
                    </button>
                    <button type="button" className="iconbtn2 danger" aria-label={t('deleteGroup')}
                      title={t('deleteGroup')}
                      onClick={() => {
                        // eslint-disable-next-line no-alert
                        if (window.confirm(t('deleteGroupConfirm', { name: g.name }))) dropGroup.mutate(g);
                      }}>
                      <TrashIcon />
                    </button>
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        );
      })}

      <Btn kind="ghost" label={t('newGroupTab')}
        onClick={() => setGroupForm({ name: '', emoji: EMOJIS[0] })} />
      </section>

      {/* ---------- همه‌ی آدم‌ها ---------- */}
      {/* گروه‌ها آن‌طرف اعضا را نشان می‌دهند، ولی «دوستیت را قطع کن» کارِ گروه
          نیست و باید برای هر کسی در دسترس باشد — چه در گروهی باشد چه نه.
          پس فهرستِ کامل این‌جا می‌ماند، با نامِ گروه‌هایش زیرِ اسم. */}
      <section className="friends-col">
      <h3>{t('peopleSection')}</h3>
      {people.length === 0 ? <Note>{t('noFriendsYet')}</Note> : null}
      {people.map((row) => {
        if (row.kind === 'contact') {
          const c = row.contact;
          return (
            <Card key={row.key} className="friend-row">
              <Avatar name={c.name} size={30} />
              <span className="person-main">
                <span className="person-name">{c.name}</span>
                <span className="person-sub">{[...c.phones, ...c.emails].join(' · ')}</span>
              </span>
              <span className="offapp">{t('notOnAppYet')}</span>
              {/* ویرایش فقط برای غیرِاپی‌ها: اسم و راه‌های رسیدن مالِ دفترچه‌ی
                  خودِ کاربره. پروفایلِ یک عضو مالِ خودشه و دکمه‌ای نمی‌گیرد. */}
              <button type="button" className="iconbtn2" aria-label={t('editFriend')}
                title={t('editFriend')}
                onClick={() => {
                  setContactEditMsg(null);
                  setContactEdit({
                    id: c.id, name: c.name,
                    phones: c.phones.length ? [...c.phones] : [''],
                    emails: c.emails.length ? [...c.emails] : [''],
                  });
                }}>
                <PencilIcon />
              </button>
              <button type="button" className="iconbtn2 danger" aria-label={t('removeFriend')}
                title={t('removeFriend')}
                onClick={() => {
                  // eslint-disable-next-line no-alert
                  if (window.confirm(t('removeFriendConfirm', { name: c.name }))) {
                    dropContactFriend.mutate(c.id);
                  }
                }}>
                <TrashIcon />
              </button>
            </Card>
          );
        }
        const f = row.friend;
        const inGroups = groupsOfUser.get(f.profile.id);
        return (
          <Card key={row.key} className="friend-row">
            <Avatar url={f.profile.avatarUrl} name={f.profile.displayName} size={30} />
            <span className="person-main">
              <span className="person-name">{f.profile.displayName}</span>
              <span className="person-sub">
                {inGroups ? inGroups.join(' · ') : `@${f.profile.username}`}
              </span>
            </span>
            {/* گزارشِ یک کاربر (WHOSUP-168). این‌جا می‌نشیند و نه جای دیگری،
                چون تنها جای وب است که یک آدمِ حساب‌دار به‌عنوان **آدم** نشان
                داده می‌شود — قرینه‌ی `PersonSheet`ِ موبایل. */}
            {/* آیکونی و هم‌شکلِ سطلِ کنارش، نه یک دکمه‌ی متنی: این کارِ نادری
                است و نباید از «حذفِ دوست» بیشتر به چشم بیاید. */}
            <ReportButton look="icon" target={{ kind: 'profile', id: f.profile.id }}
              label={t('reportUser')} />
            {/* «مسدود کردن» کنارِ «گزارش» می‌نشیند و نه جای دیگری: آن یکی
                «یک نفرِ دیگر باید ببیند» است و این یکی «به من نشانش نده» —
                دو کارِ جدا، ولی هر دو همین‌جا لازم می‌شوند (WHOSUP-230). */}
            <BlockButton person={{ id: f.profile.id, name: f.profile.displayName }} />
            <button type="button" className="iconbtn2 danger" aria-label={t('removeFriend')}
              title={t('removeFriend')} onClick={() => removeFriend(f)}>
              <TrashIcon />
            </button>
          </Card>
        );
      })}

      {pendingOut.length > 0 ? (
        <>
          <h4>{t('requestSent')}</h4>
          {/* کسی که خودم درخواستش را فرستاده‌ام هم مسدودشدنی است — تا
              قبل از WHOSUP-261 هیچ راهی برایش نبود جز صبر تا قبول کند. */}
          {pendingOut.map((f) => (
            <div key={f.friendshipId} className="chiprow">
              <span className="dim">{f.profile.displayName}</span>
              <BlockButton person={{ id: f.profile.id, name: f.profile.displayName }} />
            </div>
          ))}
        </>
      ) : null}
      </section>
      </div>{/* /friends-cols */}

      {/* ---------- افزودن دوست: یک فرم برای همه‌ی راه‌ها (WHOSUP-148) ---------- */}
      {/* قبلاً سه حالتِ جدا بود (نام‌کاربری/شماره/ایمیل) با یک لینکِ «دستی».
          ریویو صریح خواست یکی باشد: کاربر هر چه دارد می‌دهد، اپ خودش تصمیم
          می‌گیرد و می‌گوید چه شد. */}
      {addForm ? (
        <div className="modal-backdrop" onClick={() => setAddForm(null)}>
          <Card className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('addFriend')}</h3>
            <Note>{t('addFriendHowHint')}</Note>

            <Field label={t('fieldName')} value={addForm.name}
              onChange={(v) => setAddForm((f) => (f ? { ...f, name: v } : f))}
              placeholder={t('fieldNamePlaceholder')} />
            <Field label={t('fieldPhone')} value={addForm.phone}
              onChange={(v) => setAddForm((f) => (f ? { ...f, phone: v } : f))}
              placeholder={t('fieldPhonePlaceholder')} />
            <Field label={t('fieldEmail')} type="email" value={addForm.email}
              onChange={(v) => setAddForm((f) => (f ? { ...f, email: v } : f))}
              placeholder={t('fieldEmailPlaceholder')} />
            <Field label={t('fieldWhosupUsername')} value={addForm.username}
              onChange={(v) => setAddForm((f) => (f ? { ...f, username: v } : f))}
              placeholder={t('friendUsernamePlaceholder')} />

            {/* عضوِ کدام گروه‌ها بشود — چک‌باکس، به خواستِ صریحِ ریویو. */}
            {groupList.length > 0 ? (
              <>
                <span className="field-label">{t('myGroups')}</span>
                {groupList.map((g) => (
                  <Checkbox key={g.id} label={`${g.emoji} ${g.name}`.trim()}
                    checked={addForm.groupIds.includes(g.id)}
                    onChange={(on) => setAddForm((f) => (f ? {
                      ...f,
                      groupIds: on
                        ? [...f.groupIds, g.id]
                        : f.groupIds.filter((id) => id !== g.id),
                    } : f))} />
                ))}
              </>
            ) : null}

            {/* قبلاً هر دو جور پیام یک متنِ خاکستریِ کم‌رنگ بود و موفقیت عملاً
                دیده نمی‌شد — «فیلد خالی شد و هیچی نگفت» (WHOSUP-149). تیکِ
                داخلِ خودِ متن‌هاست (friendAdded/addFoundOnApp)، پیشوند لازم
                نیست. */}
            {requestMsg ? (
              <Note tone={requestMsg.ok ? 'ok' : 'warn'}>{requestMsg.text}</Note>
            ) : null}
            {addForm.name.trim() ? null : <p className="dim">{t('nameRequired')}</p>}

            <div className="chiprow">
              <Btn label={addPerson.isPending ? t('saving') : t('addFriendBtn')}
                disabled={!addForm.name.trim() || addPerson.isPending}
                onClick={() => { setRequestMsg(null); addPerson.mutate(addForm); }} />
              <Btn kind="ghost" label={t('contactsPrivacyCancel')} onClick={() => setAddForm(null)} />
            </div>
          </Card>
        </div>
      ) : null}

      {/* ---------- ویرایش (WHOSUP-144) / افزودنِ (WHOSUP-148) دوستِ غیرِاپی ---------- */}
      {/* قرینه‌ی فرمِ «ویرایش/افزودن دستی» موبایل (friend/add.tsx)، منهای
          عکس و شبکه‌های اجتماعی که وب اصلاً نشانشان نمی‌دهد. */}
      {contactEdit ? (
        <div className="modal-backdrop" onClick={() => setContactEdit(null)}>
          <Card className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{contactEdit.id != null ? t('editFriendTitle') : t('addFriendTitle')}</h3>
            {/* همان توضیحی که موبایل پشتِ «؟» دارد: این دوستی یک‌طرفه است و
                خبری به کسی نمی‌رود. در ویرایش لازم نیست — آن‌جا آدم از قبل
                توی فهرست است. */}
            {contactEdit.id == null ? <Note>{t('contactsOneWayNote')}</Note> : null}
            <Field label={t('fieldName')} value={contactEdit.name}
              onChange={(v) => setContactEdit((f) => (f ? { ...f, name: v } : f))}
              placeholder={t('fieldNamePlaceholder')} />
            <MultiField label={t('fieldPhone')} values={contactEdit.phones}
              placeholder={t('fieldPhonePlaceholder')}
              onChange={(v) => setContactEdit((f) => (f ? { ...f, phones: v } : f))} />
            <MultiField label={t('fieldEmail')} values={contactEdit.emails} type="email"
              placeholder={t('fieldEmailPlaceholder')}
              onChange={(v) => setContactEdit((f) => (f ? { ...f, emails: v } : f))} />
            {contactEditMsg ? <p className="dim">{contactEditMsg}</p> : null}
            {contactEdit.name.trim() ? null : <p className="dim">{t('nameRequired')}</p>}
            <div className="chiprow">
              <Btn label={saveContactEdit.isPending ? t('saving')
                : contactEdit.id != null ? t('save') : t('addFriendBtn')}
                disabled={!contactEdit.name.trim() || saveContactEdit.isPending}
                onClick={() => { setContactEditMsg(null); saveContactEdit.mutate(contactEdit); }} />
              <Btn kind="ghost" label={t('contactsPrivacyCancel')}
                onClick={() => setContactEdit(null)} />
            </div>
          </Card>
        </div>
      ) : null}

      {/* ---------- ساخت/ویرایشِ گروه ---------- */}
      {groupForm ? (
        <div className="modal-backdrop" onClick={() => setGroupForm(null)}>
          <Card className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{groupForm.id ? t('groupSettings') : t('newGroup')}</h3>
            <Field label={t('groupNameLabel')} value={groupForm.name}
              onChange={(v) => setGroupForm((f) => (f ? { ...f, name: v } : f))}
              placeholder={t('groupNamePlaceholder')} />
            <span className="field-label">{t('groupEmojiLabel')}</span>
            <div className="chiprow chips-wrap">
              {EMOJIS.map((e) => (
                <button key={e} type="button" className={`chip${groupForm.emoji === e ? ' on' : ''}`}
                  onClick={() => setGroupForm((f) => (f ? { ...f, emoji: e } : f))}>{e}</button>
              ))}
            </div>
            <span className="field-label">{t('category')}</span>
            {groupTagChoices.length === 0 && !hasTagSuggestQuery(groupForm.name)
              ? <p className="dim">{t('groupTagsHint')}</p> : null}
            <div className="chiprow chips-wrap">
              {groupTagChoices.map((tag) => (
                <button key={tag.id} type="button"
                  className={`chip${groupForm.categoryId === tag.id ? ' on' : ''}`}
                  onClick={() => setGroupForm((f) => (f
                    ? { ...f, categoryId: f.categoryId === tag.id ? null : tag.id } : f))}>
                  {tagLabel(tag)}
                </button>
              ))}
            </div>
            <div className="chiprow">
              <Btn label={groupForm.id ? t('save') : t('createGroup')}
                disabled={!groupForm.name.trim() || saveGroup.isPending}
                onClick={() => saveGroup.mutate(groupForm)} />
              <Btn kind="ghost" label={t('contactsPrivacyCancel')} onClick={() => setGroupForm(null)} />
            </div>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
